/**
 * Shop stages (docs/shop/PRD.md §7.1, ruling R8). Shop owns these and never reads or writes
 * Field Service ticket statuses. `correspondsToTicketStatus` documents the one designed seam
 * for any future connection; nothing reads it at runtime.
 */
export type ShopStageKey =
  | 'intake' | 'diagnosing' | 'awaiting_approval' | 'approved' | 'waiting_on_parts'
  | 'ready_for_bench' | 'repairing' | 'ready_for_pickup' | 'billing_review' | 'invoiced' | 'cancelled';

export const SHOP_STAGES: ReadonlyArray<{ key: ShopStageKey; label: string; correspondsToTicketStatus: string | null }> = [
  { key: 'intake', label: 'Checking in', correspondsToTicketStatus: null },
  { key: 'diagnosing', label: 'Diagnosing', correspondsToTicketStatus: '1.1 To Quote' },
  { key: 'awaiting_approval', label: 'Awaiting approval', correspondsToTicketStatus: '1.2 Quote Sent' },
  { key: 'approved', label: 'Approved', correspondsToTicketStatus: '1.3 Quote Approved' },
  { key: 'waiting_on_parts', label: 'Waiting on parts', correspondsToTicketStatus: '1.4 Preparing Job' },
  { key: 'ready_for_bench', label: 'Ready for bench', correspondsToTicketStatus: '1.5 Ready to Schedule' },
  { key: 'repairing', label: 'Repairing', correspondsToTicketStatus: '4.2 In Progress' },
  { key: 'ready_for_pickup', label: 'Ready for pickup', correspondsToTicketStatus: '5 Completed' },
  { key: 'billing_review', label: 'Billing review', correspondsToTicketStatus: '6 Reviewed' },
  { key: 'invoiced', label: 'Invoiced', correspondsToTicketStatus: '7 Billed' },
  { key: 'cancelled', label: 'Cancelled', correspondsToTicketStatus: '9 Cancelled' },
];

/** A Map, not an object literal: stage values arrive from request payloads. */
const BY_KEY: ReadonlyMap<string, { label: string; order: number }> = new Map(
  SHOP_STAGES.map((s, i) => [s.key, { label: s.label, order: s.key === 'cancelled' ? -1 : i }] as const),
);

export const SHOP_BOARD_STAGES: readonly ShopStageKey[] = SHOP_STAGES.filter((s) => s.key !== 'cancelled').map((s) => s.key);
export const TERMINAL_SHOP_STAGES: ReadonlySet<ShopStageKey> = new Set<ShopStageKey>(['invoiced', 'cancelled']);

export function isShopStageKey(value: unknown): value is ShopStageKey {
  return typeof value === 'string' && BY_KEY.has(value);
}
export function shopStageLabel(key: ShopStageKey): string {
  return (BY_KEY.get(key) as { label: string }).label;
}
export function shopStageOrder(key: ShopStageKey): number {
  return (BY_KEY.get(key) as { order: number }).order;
}
